import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CriticaOperadoraIntermediaria } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import { AgFilterUtils, buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, filter, first, takeUntil } from 'rxjs/operators';
import { CriticaOperadoraIntermediariaService } from './critica-operadora-intermediaria.service';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'ANALISES.CRITICA_OPERADORA_INTERMEDIARIA';

const COLUMNS: ColDef[] = [
  {
    headerName: 'Nome OPS intermediária',
    field: 'nm_operadora',
  },
  {
    headerName: '006-Registro ANS',
    field: 'nr_registro_ans_intermediaria',
  },
  {
    headerName: '014-CNPJ/CPF Executante',
    field: 'nr_cnpj_cpf_exec',
    type: 'cpfCnpj',
  },
  {
    headerName: 'Nome do prestador',
    field: 'nm_prestador_exec',
  },
  {
    headerName: 'Desc. crítica',
    field: 'ds_critica',
  },
  {
    headerName: 'Tipo de impacto',
    field: 'nm_tp_impacto',
  },
  {
    headerName: 'Qtde. críticas',
    field: 'qt_critica',
  },
  {
    headerName: 'Seq. lançamento',
    field: 'nr_seq_guia',
  },
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    hide: true,
  },
];

@Component({
    templateUrl: './critica-operadora-intermediaria.component.html',
    styleUrls: ['./critica-operadora-intermediaria.component.scss'],
    providers: [CriticaOperadoraIntermediariaService, DecimalPipe, DatePipe],
    standalone: false
})
export class CriticaOperadoraIntermediariaComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<Row>;

  gridOptions: GridOptions<Row>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];

  rowData: Row[];
  dtUltimoProcessamento: string = null;

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private permissaoService: PermissaoService,
    private service: CriticaOperadoraIntermediariaService,
    private decimalPipe: DecimalPipe,
    private datePipe: DatePipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private geralService: GeralService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : null,
          params.competencia_final ? '' + params.competencia_final : null,
        ];

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<Row>(
      {
        columnDefs: COLUMNS,
        pipes: {
          decimal: this.decimalPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    // Remove filters from url
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    this.filterChanged = false;

    // Trata o comportamento do componente visual do carregamento
    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.competenciaRange[0] || !this.competenciaRange[1]) {
      this.rowData = [];
      this.dtUltimoProcessamento = null;
      this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
      return;
    }

    const grid$ = this.service.getData(this.operadora, this.competenciaRange[0], this.competenciaRange[1]);
    const ultimo$ = this.service.getUltimoProcessamento(this.operadora).pipe(catchError(() => of(null)));

    forkJoin({ items: grid$, ultimo: ultimo$ }).subscribe({
      next: ({ items, ultimo }) => {
        this.dtUltimoProcessamento = this.getDataUltimoProcessamento(ultimo);

        if (!items?.length) {
          this.rowData = [];
          this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
          return;
        }

        this.rowData = items.map(item => ({
          ...item,
          nm_tp_impacto: this.geralService.buildNomeDeDominio(item.tp_impacto, 'MON_TIPO_IMPACTO'),
        }));
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });
  }

  private getDataUltimoProcessamento(ultimoProcessamentoResponse: {
    items?: Array<{ dt_atualizacao_mon_guia?: string | number }>;
  }): string {
    const valor = ultimoProcessamentoResponse?.items?.[0]?.dt_atualizacao_mon_guia;
    if (!valor) {
      return null;
    }

    const timestamp = Number(valor);
    return this.datePipe.transform(Number.isNaN(timestamp) ? valor : timestamp, 'dd/MM/yyyy');
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    this.filterChanged = true;
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel(): void {
    const fileNamePrefix = `critica-operadora-intermediaria-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const headers = [
      'Críticas por operadora intermediária',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

export interface Row extends CriticaOperadoraIntermediaria {
  nm_tp_impacto: string;
}
